package br.com.texeratx.boardgametimeapp.ui.screens.dashboard

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Logout
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppImagePlaceholder
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppLoadingOverlay
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppSectionCard
import br.com.texeratx.boardgametimeapp.ui.components.dashboard.DashboardHeroHeader
import br.com.texeratx.boardgametimeapp.ui.components.dashboard.DashboardSectionHeader
import br.com.texeratx.boardgametimeapp.ui.components.dashboard.Pill
import br.com.texeratx.boardgametimeapp.ui.screens.dashboard.model.DashboardGameUI
import br.com.texeratx.boardgametimeapp.ui.screens.dashboard.model.DashboardPlayerUI
import br.com.texeratx.boardgametimeapp.ui.screens.dashboard.model.LastMatchesUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDateTime

@Composable
fun DashboardUI(
    state: DashboardUiState,
    onDismissError: () -> Unit,
    onTopGamesClick: () -> Unit,
    onTopPlayersClick: () -> Unit,
    onLogout: () -> Unit,
    onGameClick: (Long) -> Unit,
    onPlayerClick: (Long) -> Unit,
    onStatsClick: () -> Unit,
    clanName: String,
    clanImageUrl: String?,
    onClanClick: () -> Unit,
    onLastMatchesClick: () -> Unit,
    onLastMatchClick: (LastMatchesUI) -> Unit,
) {
    val S = LocalStrings.current

    Box(modifier = Modifier.fillMaxSize()) {
        LazyColumn(
            modifier = Modifier.fillMaxSize(),
            contentPadding = PaddingValues(horizontal = 16.dp, vertical = 16.dp),
            verticalArrangement = Arrangement.spacedBy(18.dp)
        ) {
            item {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    DashboardHeroHeader(
                        clanName = clanName,
                        clanImageUrl = clanImageUrl,
                        onClanClick = onClanClick
                    )

                    Spacer(Modifier.weight(1f))

                    IconButton(onClick = onLogout) {
                        Icon(
                            imageVector = Icons.Default.Logout,
                            contentDescription = "Logout",
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }

            item {
                MonthSummarySection(
                    matches = state.currentMonthMatches,
                    minutes = state.currentMonthMinutes,
                    onStatsClick = onStatsClick
                )
            }

            item {
                TopPlayersSectionNew(
                    players = state.topPlayers,
                    onTopPlayersClick = onTopPlayersClick,
                    onPlayerClick = onPlayerClick
                )
            }

            item {
                TopGamesSectionNew(
                    games = state.topGames,
                    onTopGamesClick = onTopGamesClick,
                    onGameClick = onGameClick
                )
            }

            item {
                LastMatchesSectionNew(
                    matches = state.lastMatches,
                    onLastMatchesClick = onLastMatchesClick,
                    onMatchClick = onLastMatchClick
                )
            }

            item { Spacer(Modifier.height(70.dp)) }
        }

        AppLoadingOverlay(isVisible = state.isLoading)

        state.error?.let { message ->
            AlertDialog(
                onDismissRequest = { onDismissError() },
                confirmButton = {
                    TextButton(onClick = { onDismissError() }) { Text(S.ok) }
                },
                title = { Text(S.errorLoading) },
                text = { Text(message) }
            )
        }
    }
}

@Composable
private fun MonthSummarySection(
    matches: Int,
    minutes: Int,
    onStatsClick: () -> Unit
) {
    val S = LocalStrings.current

    DashboardSectionHeader(
        title = S.dashboardMonthSummary,
        onAction = onStatsClick
    )

    Spacer(Modifier.height(10.dp))

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(12.dp)
    ) {
        StatCardNew(
            title = S.dashboardMatches,
            value = matches.toString(),
            modifier = Modifier.weight(1f),
            onClick = onStatsClick
        )
        StatCardNew(
            title = S.dashboardMinutes,
            value = minutes.toString(),
            modifier = Modifier.weight(1f),
            onClick = onStatsClick
        )
    }
}

@Composable
private fun StatCardNew(
    title: String,
    value: String,
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null
) {
    AppSectionCard(
        modifier = modifier.height(86.dp),
        shape = RoundedCornerShape(16.dp),
        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 14.dp),
        onClick = onClick,
    ) {
        Column {
            Text(
                text = value,
                style = MaterialTheme.typography.headlineSmall.copy(fontWeight = FontWeight.Bold),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Spacer(Modifier.height(2.dp))
            Text(
                text = title,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

@Composable
private fun TopPlayersSectionNew(
    players: List<DashboardPlayerUI>,
    onTopPlayersClick: () -> Unit,
    onPlayerClick: (Long) -> Unit
) {
    val S = LocalStrings.current

    DashboardSectionHeader(
        title = S.dashboardTopPlayers,
        onAction = onTopPlayersClick
    )

    Spacer(Modifier.height(10.dp))

    if (players.isEmpty()) {
        AppSectionCard(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
            Text(
                text = S.noDataYet,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(4.dp)
            )
        }
        return
    }

    LazyRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        items(players) { player ->
            AppSectionCard(
                modifier = Modifier.size(width = 180.dp, height = 115.dp),
                shape = RoundedCornerShape(16.dp),
                contentPadding = PaddingValues(12.dp),
                onClick = { onPlayerClick(player.playerId) },
            ) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    if (!player.imageUrl.isNullOrBlank()) {
                        PlayerAvatar(
                            imageUrl = player.imageUrl,
                            contentDescription = player.name,
                            modifier = Modifier
                                .size(44.dp)
                                .clip(CircleShape),
                        )
                    } else {
                        Box(
                            modifier = Modifier
                                .size(44.dp)
                                .clip(CircleShape)
                                .background(MaterialTheme.colorScheme.surfaceVariant),
                            contentAlignment = Alignment.Center
                        ) {
                            Text(
                                player.name.first().uppercase(),
                                fontWeight = FontWeight.Bold,
                            )
                        }
                    }

                    Spacer(Modifier.width(10.dp))

                    Column(Modifier.weight(1f)) {
                        Text(
                            text = player.nickname ?: player.name,
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis,
                        )

                        player.nickname?.let {
                            Text(
                                text = player.name,
                                style = MaterialTheme.typography.bodyMedium,
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                        }

                        Spacer(Modifier.height(6.dp))

                        Text(
                            S.winsFormat(player.wins),
                            style = MaterialTheme.typography.bodySmall,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                        Text(
                            S.winrateFormat((player.winRate * 100).toInt()),
                            style = MaterialTheme.typography.bodySmall,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun TopGamesSectionNew(
    games: List<DashboardGameUI>,
    onTopGamesClick: () -> Unit,
    onGameClick: (Long) -> Unit
) {
    val S = LocalStrings.current

    DashboardSectionHeader(
        title = S.dashboardMostPlayed,
        onAction = onTopGamesClick
    )

    Spacer(Modifier.height(10.dp))

    if (games.isEmpty()) {
        AppSectionCard(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
            Text(
                text = S.noDataYet,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(4.dp)
            )
        }
        return
    }

    LazyRow(horizontalArrangement = Arrangement.spacedBy(12.dp)) {
        items(games) { game ->
            AppSectionCard(
                modifier = Modifier.size(width = 170.dp, height = 240.dp),
                shape = RoundedCornerShape(16.dp),
                contentPadding = PaddingValues(horizontal = 12.dp, vertical = 14.dp),
                onClick = { onGameClick(game.gameId) },
            ) {
                Column(modifier = Modifier.fillMaxSize()) {
                    if (!game.imageUrl.isNullOrBlank()) {
                        GameThumb(
                            imageUrl = game.imageUrl,
                            contentDescription = game.name,
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(96.dp)
                                .clip(MaterialTheme.shapes.medium)
                        )
                    } else {
                        AppImagePlaceholder(
                            text = S.noImage,
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(80.dp),
                            shape = MaterialTheme.shapes.medium,
                        )
                    }

                    Spacer(Modifier.height(10.dp))

                    Text(
                        text = game.name,
                        fontWeight = FontWeight.Bold,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis,
                        lineHeight = MaterialTheme.typography.bodyLarge.lineHeight,
                    )

                    Spacer(Modifier.weight(1f))

                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.Start
                    ) {
                        Pill(text = S.matchesCountFormat(game.count))
                    }
                }
            }
        }
    }
}

@Composable
private fun LastMatchesSectionNew(
    matches: List<LastMatchesUI>,
    onLastMatchesClick: () -> Unit,
    onMatchClick: (LastMatchesUI) -> Unit,
) {
    val S = LocalStrings.current

    DashboardSectionHeader(
        title = S.dashboardLastMatches,
        onAction = onLastMatchesClick
    )

    Spacer(Modifier.height(10.dp))

    if (matches.isEmpty()) {
        AppSectionCard(shape = RoundedCornerShape(16.dp), modifier = Modifier.fillMaxWidth()) {
            Text(
                text = S.noMatchesYet,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.padding(4.dp)
            )
        }
        return
    }

    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        matches.forEach { match ->
            AppSectionCard(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(120.dp),
                shape = RoundedCornerShape(16.dp),
                contentPadding = PaddingValues(start = 16.dp, top = 12.dp, end = 16.dp, bottom = 12.dp),
                onClick = { onMatchClick(match) },
            ) {
                Row(
                    modifier = Modifier.fillMaxSize(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (!match.gameImageUrl.isNullOrBlank()) {
                        GameThumb(
                            imageUrl = match.gameImageUrl,
                            contentDescription = match.gameName,
                            modifier = Modifier
                                .size(56.dp)
                                .clip(MaterialTheme.shapes.medium)
                        )
                    } else {
                        AppImagePlaceholder(
                            text = S.placeholderImg,
                            modifier = Modifier.size(56.dp),
                            shape = MaterialTheme.shapes.medium,
                        )
                    }

                    Spacer(Modifier.width(12.dp))

                    Column(
                        modifier = Modifier.weight(1f),
                        verticalArrangement = Arrangement.Center
                    ) {
                        Text(
                            text = match.gameName,
                            fontWeight = FontWeight.Bold,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )

                        Spacer(Modifier.height(6.dp))

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Pill(text = apiToUiDateTime(match.date))
                            Pill(text = "${match.duration} min")
                        }

                        Spacer(Modifier.height(6.dp))

                        Text(
                            text = match.players.joinToString(", "),
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            maxLines = 2,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                }
            }
        }
    }
}
